import AddIcon from "@mui/icons-material/Add";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { Avatar, Box, Button, Chip, CircularProgress, IconButton, Popover, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useModals } from "../../../../context/ModalContext";
import { selectCurrentCompany, selectUser } from "../../../../redux/features/user/selectors/userState.selectors";
import { formatDate as formatAppDate } from "../../../../utils";
import { useClientNotes } from "../../../clients/Clients-notes/hook/useNotes";
import { INote, JobCreateDraftNote, NoteType } from "../../../clients/Clients-notes/interfaces/notes.interface";
import { useJobEventsByIdQuery } from "../../../jobs2/jobs-calendar/hooks/useJobEventsByIdQuery";
import { JobLayout } from "../../jobs-list/jobs-layout/Job.layout";

type JobNotesCardProps = {
  jobId?: number | null;
  clientUid?: string | null;
  jobEventUid?: string | null;
  mode?: "remote" | "draft";
  variant?: "card" | "popover";
  popoverTitle?: string;
  buttonSx?: object;
  maxNotes?: number;
  draftNotes?: JobCreateDraftNote[];
  onDraftNotesChange?: (notes: JobCreateDraftNote[]) => void;
  defaultNoteType?: NoteType;
  showTypePicker?: boolean;
  emptyMessage?: string;
  placeholder?: string;
  sx?: object;
};

const NOTE_TYPE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  job: { label: "Job", color: "#0284c7", bg: "#e0f2fe" },
  visit: { label: "Visit", color: "#0f766e", bg: "#ccfbf1" },
  general: { label: "General", color: "var(--fx-surface-textsecondary, #64748B)", bg: "#f1f5f9" },
  followup: { label: "Follow-up", color: "#d97706", bg: "#fef3c7" },
  urgent: { label: "Urgent", color: "#dc2626", bg: "#fee2e2" },
  billing: { label: "Billing", color: "#7c3aed", bg: "#f3e8ff" },
};

export function JobNotesCard({
  jobId,
  clientUid,
  jobEventUid,
  mode = "remote",
  variant = "card",
  popoverTitle,
  buttonSx,
  maxNotes,
  draftNotes = [],
  onDraftNotesChange,
  defaultNoteType = "job",
  showTypePicker = true,
  emptyMessage,
  placeholder,
  sx,
}: JobNotesCardProps) {
  const isDraft = mode === "draft";
  const isPopover = variant === "popover";
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const user = useSelector(selectUser);
  const currentCompany = useSelector(selectCurrentCompany);
  const companyTimeZone = currentCompany?.timeZone || "UTC";
  const { openModal } = useModals();

  const {
    notes: remoteNotes,
    isLoading: remoteLoading,
    createNote,
    updateNote,
    togglePin,
    deleteNote,
    isSaving: remoteSaving,
  } = useClientNotes(isDraft ? null : { clientUid, jobId, jobEventUid });

  const notes = useMemo(() => (isDraft ? draftNotes : remoteNotes), [isDraft, draftNotes, remoteNotes]);

  const isVisitSpecific = Boolean(jobEventUid);

  const effectiveJobId = useMemo(() => {
    if (isVisitSpecific) return 0;
    if (jobId) return Number(jobId);
    const foundNoteWithJob = (notes as INote[]).find((n) => n?.jobId);
    return foundNoteWithJob?.jobId ? Number(foundNoteWithJob.jobId) : 0;
  }, [isVisitSpecific, jobId, notes]);

  const { events: jobEvents = [] } = useJobEventsByIdQuery(effectiveJobId);

  const eventsByUid = useMemo(() => {
    const map = new Map<string, any>();
    if (Array.isArray(jobEvents)) {
      jobEvents.forEach((evt: any) => {
        if (evt?.uid) {
          map.set(String(evt.uid), evt);
        }
      });
    }
    return map;
  }, [jobEvents]);

  const handleOpenVisit = (targetEventUid: string, noteJobId?: number | null) => {
    const resolvedJobId = noteJobId || effectiveJobId || jobId;
    if (!resolvedJobId || !targetEventUid) return;

    const matchedEvt = eventsByUid.get(String(targetEventUid));
    const visitLabel = matchedEvt?.startDate
      ? `Visit on ${formatAppDate(matchedEvt.startDate, companyTimeZone, "MMM d, yyyy")}`
      : `Visit - Job #${resolvedJobId}`;

    const modalKey = `Calendar-Visit-Stepper-${resolvedJobId}-${targetEventUid}`;
    openModal(
      {
        key: modalKey,
        label: visitLabel,
      },
      <JobLayout parentId={resolvedJobId} currentEventUid={targetEventUid} showCalendar={false} />,
    );
  };
  const isLoading = isDraft ? false : remoteLoading;
  const isSaving = isDraft ? false : remoteSaving;
  const noteLimit = maxNotes ?? (isDraft ? 1 : undefined);
  const reachedNoteLimit = Boolean(noteLimit && notes.length >= noteLimit);

  const draftAuthor = useMemo(() => {
    const fullName = [user?.firstName?.trim(), user?.lastName?.trim()].filter(Boolean).join(" ");
    return {
      uid: user?.uid || "draft",
      name: fullName || user?.email || "You",
      avatar: user?.avatar || null,
      email: user?.email || "",
    };
  }, [user]);

  const [isAdding, setIsAdding] = useState(false);
  const [newContent, setNewContent] = useState("");
  const [newType, setNewType] = useState<NoteType>(defaultNoteType);
  const [newPinned, setNewPinned] = useState(false);

  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editType, setEditType] = useState<NoteType>(defaultNoteType);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteNoteUid, setDeleteNoteUid] = useState<string | null>(null);

  const handleStartAdd = () => {
    setNewContent("");
    setNewType(defaultNoteType);
    setNewPinned(false);
    setIsAdding(true);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewContent("");
  };

  const handleSaveAdd = async () => {
    if (!newContent.trim()) return;
    if (isDraft && reachedNoteLimit) return;

    if (isDraft) {
      const nextNote: JobCreateDraftNote = {
        uid: `draft-${Date.now()}`,
        content: newContent.trim(),
        type: newType,
        pinned: newPinned,
        createdAt: new Date().toISOString(),
        author: draftAuthor,
        jobId,
        jobEventUid,
      };
      onDraftNotesChange?.([...draftNotes, nextNote]);
      setNewContent("");
      setIsAdding(false);
      return;
    }

    try {
      await createNote({
        content: newContent.trim(),
        type: newType,
        pinned: newPinned,
        jobId,
        clientUid: clientUid || "",
        jobEventUid,
      });
      setNewContent("");
      setIsAdding(false);
    } catch (err) {
      console.error("Failed to add note:", err);
    }
  };

  const handleStartEdit = (note: INote | JobCreateDraftNote) => {
    setEditingNoteId(note.uid);
    setEditContent(note.content);
    setEditType((note.type as NoteType) || "general");
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditContent("");
  };

  const handleSaveEdit = async (noteUid: string) => {
    if (!editContent.trim()) return;

    if (isDraft) {
      onDraftNotesChange?.(draftNotes.map((note) => (note.uid === noteUid ? { ...note, content: editContent.trim(), type: editType } : note)));
      setEditingNoteId(null);
      return;
    }

    try {
      await updateNote({
        noteUid,
        content: editContent.trim(),
        type: editType,
      });
      setEditingNoteId(null);
    } catch (err) {
      console.error("Failed to update note:", err);
    }
  };

  const handleTogglePin = async (note: INote | JobCreateDraftNote) => {
    if (isDraft) {
      onDraftNotesChange?.(draftNotes.map((item) => (item.uid === note.uid ? { ...item, pinned: !item.pinned } : item)));
      return;
    }

    try {
      await togglePin({ noteUid: note.uid, pinned: !note.pinned });
    } catch (err) {
      console.error("Failed to toggle pin:", err);
    }
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLElement>, noteUid: string) => {
    setDeleteAnchorEl(event.currentTarget);
    setDeleteNoteUid(noteUid);
  };

  const handleConfirmDelete = async () => {
    if (!deleteNoteUid) return;

    if (isDraft) {
      onDraftNotesChange?.(draftNotes.filter((note) => note.uid !== deleteNoteUid));
      setDeleteAnchorEl(null);
      setDeleteNoteUid(null);
      return;
    }

    try {
      await deleteNote(deleteNoteUid);
    } catch (err) {
      console.error("Failed to delete note:", err);
    } finally {
      setDeleteAnchorEl(null);
      setDeleteNoteUid(null);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderCardContent = () => (
    <Box
      sx={{
        px: isPopover ? 2 : 2.5,
        py: isPopover ? 1.5 : 2,
        mx: isDraft || isPopover ? 0 : 3,
        borderRadius: 3,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        boxShadow: isDraft || isPopover ? "none" : "0 1px 3px rgba(0,0,0,0.05)",
        ...sx,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: notes.length > 0 || isAdding ? 1.5 : 0.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <StickyNote2OutlinedIcon sx={{ fontSize: 18, color: "#ff8222" }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {popoverTitle || "Notes"}
          </Typography>
          {notes.length > 0 && (
            <Chip
              label={notes.length}
              size="small"
              sx={{
                height: 18,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: "#ff8222",
                color: "white",
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center">
          {!isAdding && !reachedNoteLimit && (
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleStartAdd}
              sx={{
                fontSize: 12,
                fontWeight: 600,
                textTransform: "none",
                color: "#ff8222",
                px: 1.2,
                py: 0.3,
                borderRadius: 1.5,
                "&:hover": { bgcolor: "rgba(255, 130, 34, 0.08)" },
              }}
            >
              Add Note
            </Button>
          )}

          {isPopover && (
            <IconButton size="small" onClick={() => setPopoverAnchorEl(null)} sx={{ p: 0.4, color: "var(--fx-surface-texttertiary, #94A3B8)" }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Quick Add Form */}
      {isAdding && (
        <Box
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: 2,
            bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
          }}
        >
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            size="small"
            placeholder={placeholder || "Type a note for this job..."}
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void handleSaveAdd();
              }
              if (e.key === "Escape") handleCancelAdd();
            }}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                borderRadius: 1.5,
                fontSize: 13,
              },
            }}
          />

          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            {showTypePicker ? (
              <Stack direction="row" spacing={0.5}>
                {(Object.keys(NOTE_TYPE_CONFIG) as NoteType[]).map((t) => {
                  const conf = NOTE_TYPE_CONFIG[t];
                  const selected = newType === t;
                  return (
                    <Chip
                      key={t}
                      label={conf.label}
                      size="small"
                      onClick={() => setNewType(t)}
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: selected ? 700 : 500,
                        bgcolor: selected ? conf.bg : "transparent",
                        color: selected ? conf.color : "var(--fx-surface-textsecondary, #64748B)",
                        border: `1px solid ${selected ? conf.color : "#cbd5e1"}`,
                        cursor: "pointer",
                        "& .MuiChip-label": { px: 0.8 },
                      }}
                    />
                  );
                })}
              </Stack>
            ) : (
              <Box />
            )}

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={newPinned ? "Unpin Note" : "Pin Note"}>
                <IconButton
                  size="small"
                  onClick={() => setNewPinned(!newPinned)}
                  sx={{ color: newPinned ? "#ff8222" : "var(--fx-surface-texttertiary, #94A3B8)", p: 0.5 }}
                >
                  {newPinned ? <PushPinIcon sx={{ fontSize: 16 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>

              <Button
                size="small"
                onClick={handleCancelAdd}
                sx={{ fontSize: 12, textTransform: "none", color: "var(--fx-surface-textsecondary, #64748B)", minWidth: "auto", px: 1 }}
              >
                Cancel
              </Button>

              <Button
                size="small"
                variant="contained"
                disabled={!newContent.trim() || isSaving}
                onClick={handleSaveAdd}
                sx={{
                  fontSize: 12,
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#ff8222",
                  "&:hover": { bgcolor: "#e06d10" },
                  borderRadius: 1.5,
                  minWidth: "auto",
                  px: 1.5,
                  py: 0.4,
                }}
              >
                {isSaving ? <CircularProgress size={14} color="inherit" /> : "Save"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={20} sx={{ color: "#ff8222" }} />
        </Box>
      )}

      {/* Empty state */}
      {!isLoading && notes.length === 0 && !isAdding && (
        <Box
          onClick={handleStartAdd}
          sx={{
            py: 1.25,
            px: 1.5,
            borderRadius: 1.5,
            border: "1px dashed var(--fx-surface-borderstrong, #CBD5E1)",
            bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "all 0.15s ease",
            "&:hover": {
              borderColor: "#ff8222",
              bgcolor: "rgba(255, 130, 34, 0.04)",
              "& .add-note-action": { color: "#ff8222" },
            },
          }}
        >
          <Typography variant="body2" sx={{ color: "var(--fx-surface-textsecondary, #64748B)", fontSize: 13 }}>
            {emptyMessage ||
              (isDraft ? "No notes yet. Save one here and it will be added when you create the job." : "No notes added for this job yet.")}
          </Typography>
          <Typography
            className="add-note-action"
            variant="caption"
            sx={{
              color: "var(--fx-surface-texttertiary, #94A3B8)",
              fontWeight: 600,
              fontSize: 12,
              ml: 1,
              whiteSpace: "nowrap",
              transition: "color 0.15s ease",
            }}
          >
            + Add Note
          </Typography>
        </Box>
      )}

      {/* Notes List */}
      {!isLoading && notes.length > 0 && (
        <Stack spacing={1} sx={{ maxH: 320, overflowY: "auto", pr: 0.5 }}>
          {notes.map((note) => {
            const isEditingThis = editingNoteId === note.uid;
            const typeConf = NOTE_TYPE_CONFIG[note.type] || NOTE_TYPE_CONFIG.general;
            const authorName = note.author?.name || "Team Member";
            const authorAvatar = note.author?.avatar;

            const linkedEvent = note.jobEventUid ? eventsByUid.get(String(note.jobEventUid)) : null;
            const eventDateShort = linkedEvent?.startDate ? formatAppDate(linkedEvent.startDate, companyTimeZone, "MMM d") : null;
            const eventDateFull = linkedEvent?.startDate ? formatAppDate(linkedEvent.startDate, companyTimeZone, "MMMM d, yyyy • h:mm a") : null;
            const hasJobEvent = Boolean(note.jobEventUid);
            const showVisitDetailsLink = !isVisitSpecific && hasJobEvent;

            return (
              <Box
                key={note.uid}
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  bgcolor: note.pinned ? "rgba(255, 130, 34, 0.04)" : "var(--fx-surface-sunken, #F8FAFC)",
                  border: `1px solid ${note.pinned ? "rgba(255, 130, 34, 0.25)" : "var(--fx-surface-border, #E2E8F0)"}`,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: note.pinned ? "#ff8222" : "var(--fx-surface-borderstrong, #CBD5E1)",
                    "& .note-actions": { opacity: 1 },
                  },
                }}
              >
                {/* Note Edit Mode */}
                {isEditingThis ? (
                  <Box>
                    <TextField
                      autoFocus
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          borderRadius: 1.5,
                          fontSize: 13,
                        },
                      }}
                    />

                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      {showTypePicker ? (
                        <Stack direction="row" spacing={0.5}>
                          {(Object.keys(NOTE_TYPE_CONFIG) as NoteType[]).map((t) => {
                            const conf = NOTE_TYPE_CONFIG[t];
                            const selected = editType === t;
                            return (
                              <Chip
                                key={t}
                                label={conf.label}
                                size="small"
                                onClick={() => setEditType(t)}
                                sx={{
                                  height: 20,
                                  fontSize: 10,
                                  fontWeight: selected ? 700 : 500,
                                  bgcolor: selected ? conf.bg : "transparent",
                                  color: selected ? conf.color : "var(--fx-surface-textsecondary, #64748B)",
                                  border: `1px solid ${selected ? conf.color : "#cbd5e1"}`,
                                  cursor: "pointer",
                                  "& .MuiChip-label": { px: 0.6 },
                                }}
                              />
                            );
                          })}
                        </Stack>
                      ) : (
                        <Box />
                      )}

                      <Stack direction="row" spacing={0.5}>
                        <IconButton size="small" onClick={handleCancelEdit} sx={{ p: 0.4 }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleSaveEdit(note.uid)}
                          disabled={!editContent.trim() || isSaving}
                          sx={{ p: 0.4, color: "#ff8222" }}
                        >
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                ) : (
                  /* Note View Mode */
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" rowGap={0.5}>
                        <Avatar src={authorAvatar || undefined} sx={{ width: 20, height: 20, fontSize: 10, bgcolor: "#64748b" }}>
                          {authorName.charAt(0).toUpperCase()}
                        </Avatar>

                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 12, color: "var(--fx-surface-textsecondary, #64748B)" }}>
                          {authorName}
                        </Typography>

                        <Typography variant="caption" sx={{ fontSize: 11, color: "var(--fx-surface-texttertiary, #94A3B8)" }}>
                          {formatDate(note.createdAt)}
                        </Typography>

                        {note.type === "visit" ? (
                          showVisitDetailsLink ? (
                            <Tooltip title={eventDateFull ? `Visit on ${eventDateFull} — Click to open visit` : "Click to open visit details"}>
                              <Chip
                                icon={
                                  <CalendarMonthRoundedIcon
                                    sx={{ fontSize: "11px !important", color: `${typeConf.color} !important`, ml: "4px !important" }}
                                  />
                                }
                                label={
                                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
                                    {eventDateShort ? `Visit · ${eventDateShort}` : typeConf.label}
                                    <OpenInNewRoundedIcon sx={{ fontSize: 10, opacity: 0.7 }} />
                                  </Box>
                                }
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenVisit(note.jobEventUid!, note.jobId);
                                }}
                                sx={{
                                  height: 18,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  bgcolor: typeConf.bg,
                                  color: typeConf.color,
                                  border: `1px solid ${typeConf.color}40`,
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                  "&:hover": {
                                    bgcolor: "#99f6e4",
                                    borderColor: typeConf.color,
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 1px 4px rgba(15, 118, 110, 0.25)",
                                  },
                                  "& .MuiChip-label": { px: 0.6 },
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              label={typeConf.label}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 600,
                                bgcolor: typeConf.bg,
                                color: typeConf.color,
                                "& .MuiChip-label": { px: 0.6 },
                              }}
                            />
                          )
                        ) : (
                          <>
                            <Chip
                              label={typeConf.label}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                fontWeight: 600,
                                bgcolor: typeConf.bg,
                                color: typeConf.color,
                                "& .MuiChip-label": { px: 0.6 },
                              }}
                            />
                            {showVisitDetailsLink && (
                              <Tooltip title={eventDateFull ? `Visit on ${eventDateFull} — Click to open visit` : "Click to open visit details"}>
                                <Chip
                                  icon={
                                    <CalendarMonthRoundedIcon
                                      sx={{ fontSize: "11px !important", color: "#0f766e !important", ml: "4px !important" }}
                                    />
                                  }
                                  label={
                                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
                                      {eventDateShort ? `Visit · ${eventDateShort}` : "Visit"}
                                      <OpenInNewRoundedIcon sx={{ fontSize: 10, opacity: 0.7 }} />
                                    </Box>
                                  }
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenVisit(note.jobEventUid!, note.jobId);
                                  }}
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    bgcolor: "#ccfbf1",
                                    color: "#0f766e",
                                    border: "1px solid rgba(15, 118, 110, 0.25)",
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                    "&:hover": {
                                      bgcolor: "#99f6e4",
                                      borderColor: "#0f766e",
                                      transform: "translateY(-1px)",
                                      boxShadow: "0 1px 4px rgba(15, 118, 110, 0.25)",
                                    },
                                    "& .MuiChip-label": { px: 0.6 },
                                  }}
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Stack>

                      {/* Action Icons */}
                      <Stack
                        direction="row"
                        spacing={0.2}
                        className="note-actions"
                        sx={{
                          opacity: note.pinned ? 1 : 0.4,
                          transition: "opacity 0.15s ease",
                        }}
                      >
                        <Tooltip title={note.pinned ? "Unpin" : "Pin"}>
                          <IconButton
                            size="small"
                            onClick={() => handleTogglePin(note)}
                            sx={{ color: note.pinned ? "#ff8222" : "var(--fx-surface-texttertiary, #94A3B8)", p: 0.3 }}
                          >
                            {note.pinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleStartEdit(note)}
                            sx={{ color: "var(--fx-surface-textsecondary, #64748B)", p: 0.3 }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={(e) => handleDeleteClick(e, note.uid)} sx={{ color: "#ef4444", p: 0.3 }}>
                            <DeleteOutlinedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    {/* Note Content */}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: "var(--fx-surface-text, #0F172A)",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        pl: 3.5,
                      }}
                    >
                      {note.content}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Delete Confirmation Popover (Minimalist style) */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={() => {
          setDeleteAnchorEl(null);
          setDeleteNoteUid(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            p: 2,
            width: 260,
            borderRadius: 3,
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", mb: 0.75 }}>Delete entry?</Typography>
        <Typography sx={{ fontSize: 11.5, color: "var(--fx-surface-textsecondary, #64748B)", lineHeight: 1.45, mb: 2 }}>
          Are you sure you want to delete this record? This action cannot be undone.
        </Typography>
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
          <Button
            size="small"
            onClick={() => {
              setDeleteAnchorEl(null);
              setDeleteNoteUid(null);
            }}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "none",
              color: "var(--fx-surface-textsecondary, #64748B)",
              borderRadius: 1.5,
              py: 0.4,
              px: 1.25,
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmDelete}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: "#dc2626",
              color: "white",
              borderRadius: 2,
              boxShadow: "none",
              py: 0.5,
              px: 1.75,
              "&:hover": {
                bgcolor: "#b91c1c",
                boxShadow: "none",
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Popover>
    </Box>
  );

  if (isPopover) {
    const hasNotes = notes.length > 0;

    return (
      <>
        <Button
          size="small"
          variant={hasNotes ? "contained" : "outlined"}
          startIcon={<StickyNote2OutlinedIcon sx={{ fontSize: 15 }} />}
          onClick={(e) => {
            setPopoverAnchorEl(e.currentTarget);
            if (!hasNotes) setIsAdding(true);
          }}
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 1.5,
            py: "3px",
            px: 1.25,
            whiteSpace: "nowrap",
            ...(hasNotes
              ? {
                  bgcolor: "#ff8222",
                  color: "white",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#e06d10",
                    boxShadow: "none",
                  },
                }
              : {
                  borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  "&:hover": {
                    borderColor: "#ff8222",
                    color: "#ff8222",
                    bgcolor: "rgba(255, 130, 34, 0.04)",
                  },
                }),
            ...buttonSx,
          }}
        >
          {hasNotes ? `Notes (${notes.length})` : "+ Add Note"}
        </Button>

        <Popover
          open={Boolean(popoverAnchorEl)}
          anchorEl={popoverAnchorEl}
          onClose={() => setPopoverAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              width: 390,
              maxWidth: "92vw",
              borderRadius: 2.5,
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
              p: 0,
              mt: 0.75,
              overflow: "hidden",
            },
          }}
        >
          {renderCardContent()}
        </Popover>
      </>
    );
  }

  return renderCardContent();
}
